/**
 * SelectionTransformer: a single shared Konva Transformer node that
 * attaches to whichever shape is currently selected. This avoids giving
 * every shape its own Transformer instance (wasteful — 30 transformers
 * sitting idle for one selection) by instead pointing one shared
 * transformer at the selected node's Konva ref each time selection
 * changes.
 *
 * Rotation snaps to 15° increments by default (PRD §9.2).
 */

import { useEffect, useRef } from 'react';
import { Transformer } from 'react-konva';
import type Konva from 'konva';
import { useEditorStore } from '../../store/useEditorStore';

const ROTATION_SNAPS = Array.from({ length: 24 }, (_, i) => i * 15);

interface SelectionTransformerProps {
  stageRef: React.RefObject<Konva.Stage>;
}

export function SelectionTransformer({ stageRef }: SelectionTransformerProps) {
  const transformerRef = useRef<Konva.Transformer>(null);
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const updateObject = useEditorStore((state) => state.updateObject);

  useEffect(() => {
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    if (!stage || !transformer) return;

    if (selectedIds.length === 1) {
      const node = stage.findOne(`#${selectedIds[0]}`);
      if (node) {
        transformer.nodes([node]);
        transformer.getLayer()?.batchDraw();
        return;
      }
    }

    transformer.nodes([]);
    transformer.getLayer()?.batchDraw();
  }, [selectedIds, stageRef]);

  function handleTransformEnd() {
    const transformer = transformerRef.current;
    if (!transformer) return;
    const node = transformer.nodes()[0];
    if (!node) return;

    const id = node.id();
    if (!id) return;

    updateObject(id, { rotation: node.rotation() });
  }

  return (
    <Transformer
      ref={transformerRef}
      rotateEnabled
      resizeEnabled={false}
      rotationSnaps={ROTATION_SNAPS}
      anchorStroke="#C9A35C"
      anchorFill="#FFFEFC"
      borderStroke="#C9A35C"
      borderDash={[4, 4]}
      onTransformEnd={handleTransformEnd}
    />
  );
}
